/**
 * Vignobles › Carte (/vignobles) — carte interactive des domaines (Leaflet),
 * filtres par statut et région, volet de détail du domaine sélectionné.
 */
import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { metaApi, vignoblesApi } from '../../services/api';
import { Spinner, Button } from '../../components/ui/kit';
import { WineColorDot } from '../../components/wine/wine';
import { X, Navigation } from 'lucide-react';
import WineMap from '../../components/map/WineMap';

const STATUS_META = [
  { key: 'cave', label: 'En cave', color: '#7d5411' },
  { key: 'deguste', label: 'Dégustés', color: '#9b9797' },
  { key: 'a_decouvrir', label: 'À découvrir', color: '#c28d41' },
];

export default function Vignobles({ view }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: domains = [], isLoading } = useQuery({ queryKey: ['domains'], queryFn: () => vignoblesApi.domains() });
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: metaApi.settings });

  const [statuses, setStatuses] = useState({ cave: true, deguste: true, a_decouvrir: true });
  const [regionFilter, setRegionFilter] = useState(null);
  const [selectedId, setSelectedId] = useState(id || null);

  useEffect(() => {
    if (view === 'decouvrir') setStatuses({ cave: false, deguste: false, a_decouvrir: true });
  }, [view]);
  useEffect(() => { if (id) setSelectedId(id); }, [id]);

  const statusCounts = useMemo(() => {
    const c = { cave: 0, deguste: 0, a_decouvrir: 0 };
    domains.forEach((d) => { c[d.status] = (c[d.status] || 0) + 1; });
    return c;
  }, [domains]);

  const regionCounts = useMemo(() => {
    const m = {};
    domains.forEach((d) => { if (d.region) m[d.region] = (m[d.region] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [domains]);

  const filtered = useMemo(
    () => domains.filter((d) => statuses[d.status] && (!regionFilter || d.region === regionFilter)),
    [domains, statuses, regionFilter]
  );

  const selected = domains.find((d) => d.id === selectedId);
  const { data: detail } = useQuery({ queryKey: ['domain', selectedId], queryFn: () => vignoblesApi.domain(selectedId), enabled: !!selectedId });

  // gps peut valoir [] (établissement sans position) : on valide une vraie paire [lat, lng].
  const isLatLng = (v) => Array.isArray(v) && v.length === 2 && v.every((n) => typeof n === 'number' && !Number.isNaN(n));
  const home = isLatLng(settings?.gps) ? settings.gps : [44.324, 3.596];
  const center = isLatLng(selected?.gps) ? selected.gps : [46.6, 2.4];

  const handleMove = (domainId, latlng) => {
    vignoblesApi.setPosition(domainId, latlng.lat, latlng.lng).then(() => qc.invalidateQueries({ queryKey: ['domains'] }));
  };
  const itineraireUrl = (d) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${d.name}, ${d.city || ''} ${d.dept || ''}, France`)}`;

  if (isLoading) return <Spinner label="Chargement des vignobles…" />;

  return (
    <div className="flex h-[calc(100vh-96px)] md:h-[calc(100vh-48px)]">
      {/* Colonne de filtres */}
      <aside className="hidden md:flex flex-col w-64 shrink-0 hair-r p-5 overflow-auto">
        <p className="kicker">Géographie</p>
        <h2 className="font-title text-3xl leading-tight mt-1 mb-4">Carte des vignobles</h2>
        <div className="inline-flex rounded-md border border-hair overflow-hidden" data-testid="country-select">
          <button className="px-3 h-8 text-[13px] bg-gold100 text-gold800 border-r border-hair" data-testid="country-fr">France</button>
          <button disabled title="Bientôt disponible" className="px-3 h-8 text-[13px] text-n500/50 border-r border-hair cursor-not-allowed" data-testid="country-pt">Portugal</button>
          <button disabled title="Bientôt disponible" className="px-3 h-8 text-[13px] text-n500/50 cursor-not-allowed" data-testid="country-it">Italie</button>
        </div>

        <div className="mt-5 space-y-2">
          {STATUS_META.map((s) => (
            <label key={s.key} className="flex items-center gap-2 cursor-pointer" data-testid={`status-${s.key}`}>
              <input type="checkbox" checked={statuses[s.key]} onChange={() => setStatuses((p) => ({ ...p, [s.key]: !p[s.key] }))} className="accent-[#b68235] w-4 h-4" />
              <span className="inline-flex items-center gap-2 flex-1 text-[14px]">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color, border: '1px solid rgba(32,31,29,0.3)' }} />
                {s.label}
              </span>
              <span className="text-n600 nums text-[13px]">{statusCounts[s.key]}</span>
            </label>
          ))}
        </div>

        <button onClick={() => setRegionFilter(null)} data-testid="region-all"
          className={`mt-6 w-full flex items-center justify-between text-[14px] px-2 py-1 rounded ${!regionFilter ? 'vd-selected' : 'hover:bg-surface'}`}>
          <span className="font-medium">Tout</span><span className="text-n600 nums">{domains.length}</span>
        </button>
        <p className="kicker mt-3 mb-2">Par région</p>
        <div className="space-y-1">
          {regionCounts.map(([r, n]) => (
            <button key={r} data-testid={`region-${r}`} onClick={() => setRegionFilter(regionFilter === r ? null : r)}
              className={`w-full flex items-center justify-between text-[14px] px-2 py-1 rounded ${regionFilter === r ? 'vd-selected' : 'hover:bg-surface'}`}>
              <span>{r}</span><span className="text-n600 nums">{n}</span>
            </button>
          ))}
        </div>

        <div className="mt-auto pt-4 flex items-center gap-2 text-[13px] text-n600">
          <span className="inline-block w-3 h-3 bg-ink rotate-45" /> Maison des Monts Bleus
        </div>
      </aside>

      {/* Carte */}
      <div className="flex-1 relative min-w-0">
        <WineMap domains={filtered} center={center} zoom={selected ? 8 : 6} home={home} selectedId={selectedId} onSelect={setSelectedId} onMove={handleMove} />
      </div>

      {/* Volet domaine sélectionné */}
      {selected && (
        <aside className="hidden lg:flex flex-col w-80 shrink-0 hair-l border-l border-hair p-5 overflow-auto animate-fade-in" data-testid="domain-panel">
          <div className="flex items-start justify-between">
            <span className="vd-tag vd-tag-fill">{STATUS_META.find((s) => s.key === selected.status)?.label}</span>
            <button onClick={() => setSelectedId(null)} data-testid="close-domain-panel"><X className="w-4 h-4" strokeWidth={1.6} /></button>
          </div>
          <h3 className="font-title text-3xl mt-2 leading-tight">{selected.name}</h3>
          <p className="text-[13px] text-n600">{selected.appellations?.join(' · ')} · {selected.region}</p>
          <div className="vd-plate aspect-[16/9] w-full mt-3"><div className="vd-hatch h-full flex items-center justify-center rounded text-[12px]">Photo ou logo du domaine</div></div>

          <div className="flex items-baseline justify-between mt-4 hair-t pt-3">
            <p className="kicker">Chez nous</p>
            <span className="nums text-[13px] text-n600">{detail?.stats?.bottles ?? selected.bottles} btl</span>
          </div>
          <div className="space-y-1.5 mt-2">
            {(detail?.cuvees || []).filter((c) => c.stock > 0).map((c) => (
              <button key={c.id} onClick={() => navigate(`/cave/cuvee/${c.id}`)} data-testid={`panel-cuvee-${c.id}`} className="w-full flex items-center justify-between text-[14px] hover:bg-surface rounded px-1 py-0.5">
                <span className="flex items-center gap-2"><WineColorDot color={c.color} size={9} /> {c.name}{c.years?.length ? ` ${c.years[c.years.length - 1]}` : ''}</span>
                <span className="nums text-n600">{c.stock}</span>
              </button>
            ))}
          </div>

          <div className="mt-auto pt-4 flex gap-2">
            <a href={itineraireUrl(selected)} target="_blank" rel="noreferrer" className="vd-btn vd-btn-secondary flex-1" data-testid="itineraire-btn"><Navigation className="w-4 h-4" /> Itinéraire</a>
            <Button variant="primary" className="flex-1" onClick={() => navigate(`/vignobles/domaine/${selected.id}`)} data-testid="fiche-domaine-btn">Fiche domaine</Button>
          </div>
        </aside>
      )}
    </div>
  );
}
